import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, MessageCircle, PhoneOff, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { countryName, detectCountry, flagOf } from "../../lib/journey";
import { StepShell, PrimaryButton } from "./ui";
import { CountryPicker } from "./CountryPicker";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Field = ({ label, children, error, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-[#1f2328]">{label}</label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

const inputCls = "h-12 w-full rounded-2xl border border-neutral-200 bg-white px-4 text-[15px] outline-none transition-colors focus:border-[var(--saffron)]";

export const ContactForm = ({ initial, onSubmit, onBack, cartCount = 0, matchStatus }) => {
  const [meta, setMeta] = useState({ countries: [], priority: [] });
  const [token, setToken] = useState("");
  const detected = useMemo(detectCountry, []);
  const [f, setF] = useState({
    name: "", phone_country: detected, phone: "", email: "", residence_iso: detected,
    consent: false, updates_opt_in: false, website: "", ...initial,
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    api.get("/public/countries").then(({ data }) => {
      const countries = data.countries.map((c) => ({ ...c, name: countryName(c.iso) })).sort((a, b) => a.name.localeCompare(b.name));
      setMeta({ countries, priority: data.priority });
    });
    refreshToken();
  }, []);

  function refreshToken() {
    api.get("/public/form-token").then(({ data }) => setToken(data.token));
  }

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const validate = () => {
    const e = {};
    if (f.name.trim().length < 2) e.name = "Please tell us your name";
    if (!/^[\d\s()-]{5,18}$/.test(f.phone.trim())) e.phone = "Please enter a valid WhatsApp number";
    if (!EMAIL_RX.test(f.email.trim())) e.email = "Please enter a valid email";
    if (!f.residence_iso) e.residence = "Please choose your country";
    if (!f.consent) e.consent = "Please agree so we can share your matches";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const dial = meta.countries.find((c) => c.iso === f.phone_country)?.dial || "";
  const review = (ev) => {
    ev.preventDefault();
    setServerError("");
    if (validate()) setConfirming(true);
  };

  const submit = async () => {
    setConfirming(false);
    setBusy(true);
    try {
      await onSubmit({
        contact: {
          name: f.name.trim(), phone_country: f.phone_country, phone: f.phone.trim(), email: f.email.trim(),
          residence_country: countryName(f.residence_iso),
          consent: f.consent, updates_opt_in: f.updates_opt_in,
        },
        form_token: token,
        website: f.website,
        draft: f,
      });
    } catch (e) {
      setServerError(formatApiError(e));
      if (e.response?.status === 409) refreshToken();
      setBusy(false);
    }
  };

  return (
    <StepShell stepKey="contact" onBack={onBack} testId="screen-contact">
      <h2 className="font-display text-[1.7rem] font-medium leading-tight text-[#1f2328] sm:text-4xl" data-testid="contact-heading">
        {cartCount ? `Your MatchMaking cart has ${cartCount} project${cartCount === 1 ? "" : "s"}.` : matchStatus === "matched" || !matchStatus ? "We've found projects that may suit you." : "Where shall we send your 5–7 presentations?"}
      </h2>
      <p className="mt-2 text-[15px] text-neutral-600" data-testid="contact-subheading">
        {cartCount ? "Where shall we send your MatchMaking presentations?" : matchStatus === "matched" || !matchStatus ? "Where shall we send your matches once they're ready?" : "Our team is shortlisting them for you, based on your requirements."}
      </p>
      <div className="mt-5 rounded-2xl border border-[#25D366]/40 bg-[#25D366]/[0.07] p-4" data-testid="contact-whatsapp-only-note">
        <p className="flex items-center gap-2 font-semibold text-[#0f6d06]"><MessageCircle className="h-5 w-5" /> WhatsApp chat only. We will not call you.</p>
        <ul className="mt-2 space-y-1.5 text-sm text-neutral-700">
          <li className="flex items-start gap-2"><PhoneOff className="mt-0.5 h-4 w-4 shrink-0 text-[#138808]" /> No phone calls, no sales calls — everything is shared in writing on WhatsApp chat and email.</li>
          <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#138808]" /> Your number stays with our team only. Tell us to stop anytime, and we will.</li>
        </ul>
      </div>
      <form onSubmit={review} className="mt-7 space-y-5 pb-4" noValidate data-testid="contact-form">
        <Field label="Your name" error={errors.name} htmlFor="c-name">
          <input id="c-name" className={inputCls} value={f.name} onChange={set("name")} maxLength={80} autoComplete="name" data-testid="contact-name-input" />
        </Field>
        <Field label="WhatsApp number (for chat only)" error={errors.phone} htmlFor="c-phone">
          <div className="flex gap-2">
            <CountryPicker countries={meta.countries} priority={meta.priority} value={f.phone_country} onChange={set("phone_country")} showDial testId="contact-dial-picker" className="w-[118px] shrink-0" />
            <input id="c-phone" className={inputCls} value={f.phone} onChange={set("phone")} inputMode="tel" autoComplete="tel-national" maxLength={18} placeholder="50 123 4567" data-testid="contact-phone-input" />
          </div>
        </Field>
        <Field label="Email" error={errors.email} htmlFor="c-email">
          <input id="c-email" type="email" className={inputCls} value={f.email} onChange={set("email")} maxLength={120} autoComplete="email" data-testid="contact-email-input" />
        </Field>
        <Field label="Country of residence" error={errors.residence}>
          <CountryPicker countries={meta.countries} priority={meta.priority} value={f.residence_iso} onChange={set("residence_iso")} testId="contact-residence-picker" className="w-full" />
        </Field>
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" data-testid="contact-honeypot" />
        <div className="space-y-3 rounded-2xl bg-[var(--ivory)] p-4">
          <label className="flex cursor-pointer items-start gap-3 text-sm text-neutral-700">
            <Checkbox checked={f.consent} onCheckedChange={(v) => set("consent")(!!v)} className="mt-0.5 h-5 w-5 data-[state=checked]:border-[#138808] data-[state=checked]:bg-[#138808]" data-testid="contact-consent-checkbox" />
            <span>I agree to receive my matches and messages about this enquiry on WhatsApp chat and email, and accept the{" "}
              <Link to="/privacy" target="_blank" className="font-medium text-[#138808] underline underline-offset-2" data-testid="contact-privacy-link">Privacy Policy</Link> and{" "}
              <Link to="/terms" target="_blank" className="font-medium text-[#138808] underline underline-offset-2" data-testid="contact-terms-link">Terms of Use</Link>.</span>
          </label>
          {errors.consent && <p className="text-xs text-red-600">{errors.consent}</p>}
          <label className="flex cursor-pointer items-start gap-3 text-sm text-neutral-600">
            <Checkbox checked={f.updates_opt_in} onCheckedChange={(v) => set("updates_opt_in")(!!v)} className="mt-0.5 h-5 w-5" data-testid="contact-updates-checkbox" />
            <span>Optional: send me occasional updates on new NRI-friendly projects.</span>
          </label>
        </div>
        <p className="flex items-center gap-2 text-xs text-neutral-500"><Lock className="h-3.5 w-3.5" /> We never share your details with developers without your permission.</p>
        {serverError && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="contact-server-error">{serverError}</p>}
        <PrimaryButton type="submit" disabled={busy || !token} data-testid="contact-submit-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : cartCount ? "Send my MatchMaking on WhatsApp" : "Send me my matches"}
        </PrimaryButton>
      </form>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="confirm-number-dialog">
          <DialogTitle className="font-display text-2xl font-medium">Is this your WhatsApp number?</DialogTitle>
          <DialogDescription>Your {cartCount ? "MatchMaking presentations" : "matches"} will be sent here on WhatsApp chat. Please check every digit.</DialogDescription>
          <p className="flex items-start gap-2 rounded-2xl border border-[#138808]/30 bg-[#138808]/[0.06] px-3.5 py-3 text-sm font-medium text-[#0f6d06]" data-testid="confirm-number-no-calls-note">
            <MessageCircle className="mt-0.5 h-4 w-4 shrink-0" /> We do not call. We share everything only on WhatsApp chat, so a wrong digit means you won't receive your matches.
          </p>
          <p className="my-2 rounded-2xl bg-[var(--ivory)] px-4 py-4 text-center font-display text-2xl tracking-wide text-[#1f2328]" data-testid="confirm-number-value">
            {flagOf(f.phone_country)} +{dial} {f.phone.trim()}
          </p>
          <PrimaryButton onClick={submit} data-testid="confirm-number-yes-button">Yes, send it here</PrimaryButton>
          <button type="button" onClick={() => { setConfirming(false); setTimeout(() => document.getElementById("c-phone")?.focus(), 50); }} className="min-h-[44px] w-full rounded-full border border-neutral-300 text-sm font-semibold" data-testid="confirm-number-edit-button">
            Edit number
          </button>
        </DialogContent>
      </Dialog>
    </StepShell>
  );
};
